import { useEffect, useState } from 'react';
import { getPaidSubscriptions, getWallet } from '../cabinet.api';
import { serverTiles } from '../cabinet.mock';
import { SubscriptionPlan, WalletInfo } from '../cabinet.schemas';
import { createServerTilesWithPing } from '../cabinet.utils';

type ShopPageProps = {
  onOpenPayment: (plan: SubscriptionPlan, wallet: WalletInfo) => void;
};

function getPlanTitle(plan: SubscriptionPlan) {
  return plan.title || plan.name || '';
}

function getPlanPrice(plan: SubscriptionPlan) {
  const price = plan.price || plan.amount || '';
  return typeof price === 'number' ? `${price} ₽` : price.includes('₽') ? price : price ? `${price} ₽` : '';
}

function getNumericPrice(plan: SubscriptionPlan) {
  return Number(String(plan.price || plan.amount || 0).replace(/[^\d.]/g, '')) || 0;
}

function getPlanNote(plan: SubscriptionPlan) {
  if (plan.count_days === 365 || plan.slug === 'year_sub') {
    return 'Долгий доступ';
  }

  if (plan.slug === 'week_sub') {
    return 'Быстрый старт';
  }

  return '';
}

function getPlanDuration(plan: SubscriptionPlan) {
  const days = plan.count_days || plan.duration_days;
  return days ? `${days} дней` : '';
}

export function ShopPage({ onOpenPayment }: ShopPageProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);
  const [displayServerTiles] = useState(() => createServerTilesWithPing(serverTiles));

  useEffect(() => {
    getPaidSubscriptions()
      .then((items) => setPlans([...items].sort((a, b) => getNumericPrice(a) - getNumericPrice(b))))
      .catch(() => setPlans([]))
      .finally(() => setIsLoadingPlans(false));

    getWallet()
      .then(setWallet)
      .catch(() => setWallet(null))
      .finally(() => setIsLoadingWallet(false));
  }, []);

  return (
    <div className="cabinet-page shop-page">
      <div className="cabinet-title-row shop-title-row">
        <h1>Подписки и магазин</h1>
        {isLoadingWallet ? <span>Загружаем бонусы...</span> : wallet ? <span>Бонусы: {wallet.balance} {wallet.currency}</span> : null}
      </div>

      <div className="shop-mobile-heading">
        <div>
          <h1>Магазин</h1>
          <span>Актуальные тарифы</span>
        </div>
        <h2>🛒 Подписки MifVPN</h2>
        <p>Выберите подписку ниже, после этого сможете списать кешбэк.</p>
      </div>

      {isLoadingPlans ? (
        <section className="cabinet-loading-panel shop-loading-panel" role="status" aria-live="polite">
          <span aria-hidden="true" />
          <strong>Загружаем тарифы</strong>
        </section>
      ) : plans.length === 0 ? (
        <section className="cabinet-panel">Пока нет доступных подписок</section>
      ) : (
        <div className="cabinet-pricing-grid">
          {plans.map((plan) => (
            <article className={`cabinet-price ${plan.slug === 'month_sub' ? 'highlighted' : ''}`} key={plan.id}>
              <div className="plan-main-row">
                <h2>{getPlanTitle(plan)}</h2>
                <strong className="plan-price">{plan.count_days === 365 ? <><s>₽1200</s> {getPlanPrice(plan)}</> : getPlanPrice(plan)}</strong>
              </div>
              <div className="plan-badges">
                {getPlanNote(plan) ? <span className="plan-badge soft">{getPlanNote(plan)}</span> : null}
                {plan.slug === 'month_sub' ? <span className="plan-badge">Рекомендуем</span> : null}
                {plan.count_days === 30 ? <span className="plan-badge amber">Пользователи предпочитают</span> : null}
              </div>
              {getPlanDuration(plan) ? <span className="plan-duration">{getPlanDuration(plan)}</span> : null}
              {plan.description ? <p>{plan.description}</p> : null}
              <div className="cabinet-price-action">
                <button className={plan.slug === 'month_sub' ? 'button button-primary' : 'button button-secondary'} disabled={!wallet || isLoadingWallet} type="button" onClick={() => {
                  if (wallet) {
                    onOpenPayment(plan, wallet);
                  }
                }}>
                  {isLoadingWallet ? 'Загружаем баланс...' : 'Оформить'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <section className="cabinet-panel benefits-panel">
        <h2>Что вы получаете</h2>
        <div className="cabinet-benefits">
          <span>Безлимитный трафик</span>
          <span>До 3 устройств</span>
          <span>Стабильные серверы</span>
        </div>
      </section>

      <section className="cabinet-panel server-grid-panel shop-servers-panel">
        <div className="servers-panel-head">
          <div>
            <h2>Доступные серверы</h2>
            <p>Локации и сервисные направления для быстрого подключения.</p>
          </div>
          <span>Live status</span>
        </div>
        <div className="cabinet-server-grid shop-server-grid">
          {displayServerTiles.map((server) => (
            <div className={`cabinet-server-tile ${server.available ? 'available' : 'coming'}`} key={server.code}>
              <span className="server-icon">{server.icon}</span>
              <strong>{server.code}</strong>
              <span>{server.title}</span>
              <small>{server.ping}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="cabinet-panel faq-mini">
        <h2>FAQ</h2>
        <p>Можно ли оплатить бонусами? Да, до 50% стоимости подписки.</p>
        <p>Сколько устройств поддерживается? До трех одновременно на одном тарифе.</p>
      </section>
    </div>
  );
}
